import * as React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useIntl } from 'react-intl';

import { useListGatewayPasses } from 'src/features/gatewayPass/api/useListGatewayPasses';
import { useGetActiveStay } from 'src/features/stay/api/useGetActiveStay';
import { useListActiveStayPeriods } from 'src/features/stay/api/useListActiveStayPeriods';
import ActiveStayPeriods from 'src/features/stay/components/sidePanel/ActiveStayPeriods';
import ActiveStaySidePanelButtons from 'src/features/stay/components/sidePanel/ActiveStaySidePanelButtons';
import OverviewActiveStays from 'src/features/stay/components/sidePanel/OverviewActiveStays';
import ImageCarousel from 'src/features/visitor/components/carousel/ImageCarousel';
import SidePanelTabs from 'src/features/stay/components/sidePanel/SidePanelTabs';
import { timestampToId } from 'src/features/visitor/utils/timestampToId';
import { visitorMessages } from 'src/features/visitor/utils/messages';
import { Badge } from 'src/registry/ui/Badge';
import { Button } from 'src/registry/ui/Button';
import {
    DialogBody,
    DialogClose,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
} from 'src/registry/ui/Dialog';
import { Skeleton } from 'src/registry/ui/Skeleton';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useParking } from 'src/providers/ParkingProvider';
import { STAY_TYPE } from 'src/features/stay/types';

type ActiveStaysSidePanelProps = {
    selectedStayId?: string;
};

const ActiveStaysSidePanel = ({ selectedStayId }: ActiveStaysSidePanelProps) => {
    const { formatMessage, locale } = useIntl();

    const { parkingId } = useParking();
    const { data: selectedActiveStay } = useGetActiveStay({
        parkingId,
        stayId: selectedStayId ?? '',
        config: {
            enabled: !!selectedStayId,
        },
    });

    const { data: activeStayPeriodsData } = useListActiveStayPeriods({
        parkingId,
        stayId: selectedStayId ?? '',
        config: { enabled: !!selectedStayId },
    });

    const { data: filteredGatewayPassesByStayId } = useListGatewayPasses({
        parkingId,
        active_stay: selectedStayId ?? '',
        limit: 500,
        config: { enabled: !!selectedStayId },
    });

    const gatewayPassesPhotos = React.useMemo(() => {
        return filteredGatewayPassesByStayId?.results.map((pass) => {
            return {
                image: pass.photo,
                title: pass.gateway.translations.find((z) => z.language === locale)?.name,
                subtitle: `${formatMessage(visitorMessages.pass)} ${timestampToId(pass.time)}`,
            };
        });
    }, [filteredGatewayPassesByStayId?.results, formatMessage, locale]);

    const activePeriodsResults = activeStayPeriodsData?.results ?? [];

    return (
        <>
            {selectedActiveStay ? (
                <DialogHeader>
                    <div className="flex items-center gap-2">
                        <DialogTitle className="pr-0 font-mono">
                            {formatMessage(visitorMessages.parkingStay)}
                        </DialogTitle>
                        <Badge theme="green">
                            <FontAwesomeIcon icon={ICON_MAP.WAVE_PULSE} />
                            {formatMessage(visitorMessages.activeStay)}
                        </Badge>
                    </div>
                    <DialogDescription>{`#${selectedActiveStay.ref}`}</DialogDescription>
                </DialogHeader>
            ) : (
                <DialogHeader>
                    <Skeleton className="h-7 w-32" />
                    <Skeleton className="h-5 w-60" />
                </DialogHeader>
            )}

            <DialogSeparator />
            <DialogBody>
                <ImageCarousel imageSlides={gatewayPassesPhotos} />
                <ActiveStaySidePanelButtons stayId={selectedStayId} stayType={STAY_TYPE.ACTIVE} />
                <SidePanelTabs
                    tabs={[
                        {
                            key: 'overview',
                            label: formatMessage(visitorMessages.overview),
                            content: <OverviewActiveStays selectedStay={selectedActiveStay} />,
                        },
                        {
                            key: 'periods',
                            label: formatMessage(visitorMessages.periods),
                            content: <ActiveStayPeriods stayPeriodsData={activePeriodsResults} />,
                        },
                    ]}
                    defaultActiveKey="overview"
                />
            </DialogBody>
            <DialogFooter>
                <DialogClose asChild>
                    <Button variant="outline" className="flex-1">
                        {formatMessage(generalMessages.close)}
                    </Button>
                </DialogClose>
            </DialogFooter>
        </>
    );
};

export default ActiveStaysSidePanel;
